import * as THREE from "three";

/**
 * Convergence · isolated prototype scene.
 *
 * The frozen frame teaches the viewer how to see.
 * This phase shows what becomes possible once they do.
 *
 * Plays the full four acts of Frame 002 (withdraw · hold · rotate ·
 * settle), then adds a fifth: convergence.
 *
 * Convergence must feel like a consequence of coherence, not a
 * reward animation. Objects move toward a shared relational center;
 * they never become one object.
 *
 * Frame 002's mechanics are duplicated here, not imported, so this
 * prototype cannot cause any drift in the frozen frame.
 */


// -- Defaults ------------------------------------------------------------
//
// The Frame 002 acts are locked to Frame 002's frozen defaults.
// Only the convergence controls are exposed to the researcher.

export const CONVERGENCE_DEFAULTS = {
  // -- Frozen (copied from Frame 002) ---------------------------------
  cameraStartZ: 0.55,
  cameraEndZ: 2.90,
  cameraDuration: 12.0,
  easing: "smoothstep",
  distanceHoldDuration: 2.5,
  rotationArc: 0.20,
  rotationDuration: 4.5,
  holdDuration: 5.0,
  objectSeparation: 1.0,
  objectScale: 0.13,
  lightIntensity: 2.6,
  lightFalloff: 0.05,
  materialTransmission: 0.22,

  // -- Convergence (new, exposed) -------------------------------------
  //
  // The prototype starts conservative on purpose. Movement should
  // begin late enough that the viewer has already understood the
  // arrangement — and small enough that "removing half" would still
  // preserve the meaning.
  convergenceDelay: 2.5,          // seconds between settle and convergence start
  convergenceDuration: 14.0,      // seconds for the movement
  convergenceAmount: 0.24,        // fraction of current separation to close
};


const BASE_POSITIONS = [
  new THREE.Vector3( 0.00,  0.00, -0.35),
  new THREE.Vector3( 1.35,  0.22, -1.55),
];

const CENTROID = new THREE.Vector3(
  (BASE_POSITIONS[0].x + BASE_POSITIONS[1].x) * 0.5,
  (BASE_POSITIONS[0].y + BASE_POSITIONS[1].y) * 0.5,
  (BASE_POSITIONS[0].z + BASE_POSITIONS[1].z) * 0.5,
);

const OBJECT_COLOR = 0xf3ead6;


function easeWithdraw(name, t) {
  t = Math.max(0, Math.min(1, t));
  if (name === "linear") return t;
  if (name === "cubic") {
    const s = t * t * (3 - 2 * t);
    return s * s;
  }
  return t * t * (3 - 2 * t);
}

// Smootherstep — Perlin. C² continuous. Acceleration is smoother
// than smoothstep. Used for both the inspection arc (matching the
// frozen frame) and for the convergence.
function smootherstep(t) {
  t = Math.max(0, Math.min(1, t));
  return t * t * t * (t * (t * 6 - 15) + 10);
}


export function createConvergenceScene({ container, initialParams, onProgress }) {
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || window.innerHeight;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  const camera = new THREE.PerspectiveCamera(30, width / height, 0.01, 200);
  camera.position.set(0, 0, initialParams.cameraStartZ);
  camera.lookAt(0, 0, -1);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  envScene.background = new THREE.Color(0x040404);
  const envTex = pmrem.fromScene(envScene, 0.04).texture;
  scene.environment = envTex;

  const coreGeo = new THREE.SphereGeometry(1, 24, 24);
  const shellGeo = new THREE.SphereGeometry(1, 48, 48);
  const haloGeo = new THREE.SphereGeometry(1, 24, 24);

  const objects = BASE_POSITIONS.map(() => {
    const group = new THREE.Group();
    const coreMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(OBJECT_COLOR),
      toneMapped: false,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    group.add(core);

    const shellMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.72,
      metalness: 0,
      transmission: initialParams.materialTransmission,
      thickness: initialParams.objectScale * 2.0,
      ior: 1.42,
      attenuationColor: new THREE.Color(OBJECT_COLOR),
      attenuationDistance: 0.35,
      transparent: false,
    });
    const shell = new THREE.Mesh(shellGeo, shellMat);
    group.add(shell);

    const haloMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(OBJECT_COLOR),
      transparent: true,
      opacity: 0.03,
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    group.add(halo);

    scene.add(group);
    return { group, core, shell, halo, coreMat, shellMat, haloMat };
  });


  let currentParams = { ...initialParams };


  function applyStaticParams(p) {
    objects.forEach((o, i) => {
      // Object positions are set by the convergence step each frame.
      // Only visual attributes are set here.
      o.core.scale.setScalar(p.objectScale * 0.55);
      o.shell.scale.setScalar(p.objectScale);
      o.halo.scale.setScalar(p.objectScale * (1.35 + p.lightFalloff * 4));

      o.coreMat.color.set(OBJECT_COLOR);
      o.coreMat.color.multiplyScalar(p.lightIntensity);

      o.shellMat.transmission = p.materialTransmission;
      o.shellMat.thickness = p.objectScale * 2.0;
      o.shellMat.needsUpdate = true;

      o.haloMat.opacity = 0.012 + p.lightFalloff * 0.06;
    });
  }
  applyStaticParams(currentParams);


  // Position objects at their rest position, optionally displaced
  // toward the shared centroid by `convergeFraction * amount`.
  function positionObjects(p, convergeFraction) {
    const closeFraction = p.convergenceAmount * convergeFraction;
    objects.forEach((o, i) => {
      const rest = BASE_POSITIONS[i].clone().multiplyScalar(p.objectSeparation);
      const target = new THREE.Vector3().lerpVectors(
        rest,
        new THREE.Vector3(
          CENTROID.x * p.objectSeparation,
          CENTROID.y * p.objectSeparation,
          CENTROID.z * p.objectSeparation,
        ),
        closeFraction,
      );
      o.group.position.copy(target);
    });
  }
  positionObjects(currentParams, 0);


  // -- Playback state machine -----------------------------------------

  let playState = "idle";
  // "idle" | "withdrawing" | "holding-distance" | "rotating" | "settling"
  // | "convergence-delay" | "converging" | "done"
  let phaseT = 0;

  function placeCameraForRotation(p, arcT) {
    const theta = p.rotationArc * arcT;
    const dx = 0 - CENTROID.x;
    const dz = p.cameraEndZ - CENTROID.z;
    const cosR = Math.cos(theta);
    const sinR = Math.sin(theta);
    const newX = CENTROID.x + dx * cosR - dz * sinR;
    const newZ = CENTROID.z + dx * sinR + dz * cosR;
    camera.position.set(newX, 0, newZ);
    camera.lookAt(CENTROID.x, CENTROID.y, CENTROID.z);
  }

  function startPlayback() {
    playState = "withdrawing";
    phaseT = 0;
    camera.position.set(0, 0, currentParams.cameraStartZ);
    camera.lookAt(0, 0, -1);
    positionObjects(currentParams, 0);
  }
  function stopPlayback() {
    playState = "idle";
    phaseT = 0;
    camera.position.set(0, 0, currentParams.cameraStartZ);
    camera.lookAt(0, 0, -1);
    positionObjects(currentParams, 0);
  }
  function jumpToPreConvergence() {
    // Land at the end of Act 4, before convergence begins.
    playState = "convergence-delay";
    phaseT = 0;
    placeCameraForRotation(currentParams, 1);
    positionObjects(currentParams, 0);
  }
  function jumpToConverged() {
    // Skip everything, show the final converged state.
    playState = "done";
    phaseT = 0;
    placeCameraForRotation(currentParams, 1);
    positionObjects(currentParams, 1);
  }


  const clock = new THREE.Clock();
  let running = true;

  function loop() {
    if (!running) return;
    requestAnimationFrame(loop);
    const dt = clock.getDelta();
    const p = currentParams;

    if (
      playState === "withdrawing" ||
      playState === "holding-distance" ||
      playState === "rotating" ||
      playState === "settling" ||
      playState === "convergence-delay" ||
      playState === "converging"
    ) {
      phaseT += dt;
    }

    if (playState === "withdrawing") {
      const t = Math.min(1, phaseT / Math.max(0.5, p.cameraDuration));
      const eased = easeWithdraw(p.easing, t);
      camera.position.set(0, 0, p.cameraStartZ + (p.cameraEndZ - p.cameraStartZ) * eased);
      camera.lookAt(0, 0, -1);
      if (t >= 1) { playState = "holding-distance"; phaseT = 0; }
    } else if (playState === "holding-distance") {
      camera.position.set(0, 0, p.cameraEndZ);
      camera.lookAt(0, 0, -1);
      if (phaseT >= p.distanceHoldDuration) { playState = "rotating"; phaseT = 0; }
    } else if (playState === "rotating") {
      const t = Math.min(1, phaseT / Math.max(0.3, p.rotationDuration));
      placeCameraForRotation(p, smootherstep(t));
      if (t >= 1) { playState = "settling"; phaseT = 0; }
    } else if (playState === "settling") {
      placeCameraForRotation(p, 1);
      if (phaseT >= p.holdDuration) { playState = "convergence-delay"; phaseT = 0; }
    } else if (playState === "convergence-delay") {
      // The quiet pause that confirms understanding, before movement
      // begins. Camera holds. Objects still still.
      placeCameraForRotation(p, 1);
      positionObjects(p, 0);
      if (phaseT >= p.convergenceDelay) { playState = "converging"; phaseT = 0; }
    } else if (playState === "converging") {
      const t = Math.min(1, phaseT / Math.max(0.5, p.convergenceDuration));
      // Camera does not move. Light does not change. Objects have
      // no rotation and no per-object animation — only the shared
      // interpolation toward the centroid.
      placeCameraForRotation(p, 1);
      positionObjects(p, smootherstep(t));
      if (t >= 1) { playState = "done"; phaseT = 0; }
    } else if (playState === "done") {
      placeCameraForRotation(p, 1);
      positionObjects(p, 1);
    } else {
      // idle
      camera.position.set(0, 0, p.cameraStartZ);
      camera.lookAt(0, 0, -1);
      positionObjects(p, 0);
    }

    if (onProgress) {
      const phaseDurations = {
        "idle": 0,
        "withdrawing": p.cameraDuration,
        "holding-distance": p.distanceHoldDuration,
        "rotating": p.rotationDuration,
        "settling": p.holdDuration,
        "convergence-delay": p.convergenceDelay,
        "converging": p.convergenceDuration,
        "done": 0,
      };
      const phaseDur = phaseDurations[playState] || 0;
      onProgress({
        state: playState,
        phaseT,
        phaseDuration: phaseDur,
        phaseFraction: phaseDur > 0 ? Math.min(1, phaseT / phaseDur) : (playState === "done" ? 1 : 0),
      });
    }

    renderer.render(scene, camera);
  }
  loop();


  function onResize() {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
  window.addEventListener("resize", onResize);


  function screenshot({ presetName = "unsaved", scale = 2 } = {}) {
    const oldSize = renderer.getSize(new THREE.Vector2());
    const w = Math.round(oldSize.x * scale);
    const h = Math.round(oldSize.y * scale);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL("image/png");
    renderer.setSize(oldSize.x, oldSize.y, false);
    camera.aspect = oldSize.x / oldSize.y;
    camera.updateProjectionMatrix();

    const timestamp = new Date().toISOString();
    const meta = {
      prototype: "convergence",
      timestamp,
      preset: presetName,
      cameraPosition: camera.position.toArray(),
      params: { ...currentParams },
      playState,
      phaseT,
    };
    const stamp = timestamp.replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `convergence_${stamp}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);

    const blob = new Blob([JSON.stringify(meta, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const b = document.createElement("a");
    b.href = url;
    b.download = `convergence_${stamp}.json`;
    document.body.appendChild(b); b.click(); document.body.removeChild(b);
    URL.revokeObjectURL(url);
  }


  function dispose() {
    running = false;
    window.removeEventListener("resize", onResize);
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
    coreGeo.dispose();
    shellGeo.dispose();
    haloGeo.dispose();
    objects.forEach((o) => {
      o.coreMat.dispose();
      o.shellMat.dispose();
      o.haloMat.dispose();
    });
    envTex.dispose();
    pmrem.dispose();
    renderer.dispose();
  }


  return {
    setParams(p) {
      currentParams = { ...p };
      applyStaticParams(currentParams);
    },
    play: startPlayback,
    stop: stopPlayback,
    jumpToPreConvergence,
    jumpToConverged,
    screenshot,
    dispose,
  };
}
